import React, { useState, useEffect, useRef } from 'react';
import Textarea from '../../../components/ui/Textarea';
import { useToast } from '../../../components/ui/Toast';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import notificationService from '../../../services/notificationService';
import VariablePicker from './VariablePicker';

const TemplateEditor = ({ conferenceId, template, onClose, onSave }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    notificationType: 'custom',
    subject: '',
    message: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [cursorPosition, setCursorPosition] = useState(0);
  const messageTextareaRef = React.useRef(null);
  const subjectInputRef = React.useRef(null);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template?.name || '',
        notificationType: template?.notificationType || 'custom',
        subject: template?.subject || '',
        message: template?.message || ''
      });
    }
  }, [template]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCursorPosition = (e) => {
    setCursorPosition(e?.target?.selectionStart);
  };

  const insertVariable = (variable, targetField = 'message') => {
    if (targetField === 'message') {
      const textarea = messageTextareaRef?.current;
      if (textarea) {
        const start = textarea?.selectionStart;
        const end = textarea?.selectionEnd;
        const text = formData?.message || '';
        const newText = text?.substring(0, start) + variable + text?.substring(end);
        setFormData(prev => ({ ...prev, message: newText }));
        
        // Set cursor position after variable
        setTimeout(() => {
          textarea?.focus();
          textarea?.setSelectionRange(start + variable?.length, start + variable?.length);
        }, 0);
      }
    } else if (targetField === 'subject') {
      const input = subjectInputRef?.current;
      if (input) {
        const start = input?.selectionStart;
        const end = input?.selectionEnd;
        const text = formData?.subject || '';
        const newText = text?.substring(0, start) + variable + text?.substring(end);
        setFormData(prev => ({ ...prev, subject: newText }));
        
        setTimeout(() => {
          input?.focus();
          input?.setSelectionRange(start + variable?.length, start + variable?.length);
        }, 0);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      if (template) {
        await notificationService?.updateTemplate(template?.id, formData);
      } else {
        await notificationService?.createTemplate({
          ...formData,
          conferenceId
        });
      }
      onSave?.();
    } catch (err) {
      toastError(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const notificationTypeOptions = [
    { value: 'assignment', label: 'Reviewer Assignment' },
    { value: 'decision', label: 'Paper Decision' },
    { value: 'status_update', label: 'Status Update' },
    { value: 'deadline_reminder', label: 'Deadline Reminder' },
    { value: 'custom', label: 'Custom Message' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {template ? 'Edit Template' : 'Create New Template'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Use variables like {'{{'} reviewerName {'}}'} to personalize emails
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <Icon name="X" size={20} />
        </Button>
      </div>

      {/* Form */}
      <div className="space-y-5">
        <Input
          label="Template Name"
          type="text"
          placeholder="e.g., Reviewer Assignment Notification"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Notification Type <span className="text-error">*</span>
          </label>
          <Select
            options={notificationTypeOptions}
            value={formData?.notificationType}
            onChange={(value) => handleInputChange('notificationType', value)}
          />
        </div>

        {/* Subject with Variable Picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Subject <span className="text-error">*</span>
            </label>
            <VariablePicker
              notificationType={formData?.notificationType}
              onSelect={(variable) => insertVariable(variable, 'subject')}
              compact
            />
          </div>
          <input
            ref={subjectInputRef}
            type="text"
            placeholder="Enter email subject..."
            value={formData?.subject}
            onChange={(e) => handleInputChange('subject', e?.target?.value)}
            className="flex h-12 w-full rounded-md border-2 border-input bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {errors?.subject && (
            <p className="text-xs text-destructive mt-1">{errors?.subject}</p>
          )}
        </div>

        {/* Message with Variable Picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Message <span className="text-error">*</span>
            </label>
            <VariablePicker
              notificationType={formData?.notificationType}
              onSelect={(variable) => insertVariable(variable, 'message')}
            />
          </div>
          <Textarea
            ref={messageTextareaRef}
            label="Message"
            required
            value={formData?.message || ''}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            onSelect={handleCursorPosition}
            onClick={handleCursorPosition}
            placeholder={'Enter template message…\n\nTip: use variables like {{reviewerName}}, {{paperTitle}}, {{deadline}} to personalise your emails.'}
            rows={12}
            className="font-mono"
          />
          {errors?.message && (
            <p className="text-xs text-destructive mt-1">{errors?.message}</p>
          )}
        </div>

        {/* Character Count */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Message length: {formData?.message?.length} characters</span>
          <span>Subject length: {formData?.subject?.length} characters</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} className="mr-2" />
              {template ? 'Update Template' : 'Create Template'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TemplateEditor;